import { type RefObject, useEffect } from 'react'

const FLOATING_HEADER_GAP = 8

type FloatingTableHeaderOptions = {
  enabled: boolean
  anchorRef: RefObject<HTMLElement | null>
  frameRef: RefObject<HTMLElement | null>
  floatingRef: RefObject<HTMLElement | null>
  floatingScrollRef: RefObject<HTMLElement | null>
  visibleClassName: string
  hiddenClassName: string
}

export const useFloatingTableHeader = ({
  enabled,
  anchorRef,
  frameRef,
  floatingRef,
  floatingScrollRef,
  visibleClassName,
  hiddenClassName,
}: FloatingTableHeaderOptions) => {
  useEffect(() => {
    const anchor = anchorRef.current
    const frame = frameRef.current
    const floating = floatingRef.current
    const floatingScroll = floatingScrollRef.current
    if (!enabled || !anchor || !frame || !floating || !floatingScroll) return

    const siteHeader = document.querySelector('header')
    let isVisible = false

    const getSiteHeaderBottom = () =>
      siteHeader?.getBoundingClientRect().bottom ?? 0
    const getStickyOffset = () => getSiteHeaderBottom() + FLOATING_HEADER_GAP

    const syncHorizontalScroll = () => {
      floatingScroll.scrollLeft = frame.scrollLeft
    }

    const updatePosition = () => {
      const frameRect = frame.getBoundingClientRect()
      floating.style.top = `${getSiteHeaderBottom()}px`
      floating.style.setProperty('--floating-gap', `${FLOATING_HEADER_GAP}px`)
      floating.style.left = `${frameRect.left}px`
      floating.style.width = `${frameRect.width}px`
      syncHorizontalScroll()
    }

    const setVisible = (visible: boolean) => {
      if (visible === isVisible) return
      isVisible = visible
      floating.classList.toggle(visibleClassName, visible)
      floating.classList.toggle(hiddenClassName, !visible)
    }

    const createObserver = () =>
      new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting), {
        threshold: 0,
        rootMargin: `-${Math.round(getStickyOffset())}px 0px 0px 0px`,
      })

    let observer = createObserver()
    observer.observe(anchor)
    updatePosition()

    const resizeObserver = new ResizeObserver(() => {
      updatePosition()
      observer.disconnect()
      observer = createObserver()
      observer.observe(anchor)
    })
    if (siteHeader) resizeObserver.observe(siteHeader)
    resizeObserver.observe(frame)

    window.addEventListener('scroll', updatePosition, { passive: true })
    frame.addEventListener('scroll', syncHorizontalScroll, { passive: true })

    return () => {
      observer.disconnect()
      resizeObserver.disconnect()
      window.removeEventListener('scroll', updatePosition)
      frame.removeEventListener('scroll', syncHorizontalScroll)
    }
  }, [
    enabled,
    anchorRef,
    frameRef,
    floatingRef,
    floatingScrollRef,
    visibleClassName,
    hiddenClassName,
  ])
}
